// Mini-AMP (Dok 60): ett samlat formulär för små projekt som skriver till
// exakt samma underlag som den fullständiga kedjan – ingen parallell datamodell.
import { supabase } from "@/integrations/supabase/client";
import { createAmpPlan } from "@/lib/amp/createPlan";
import { AMP_SPECIAL_RISKS } from "@/lib/amp/constants";

export interface MiniAmpSpecialRisk {
  id: string;
  text: string;
  applicable: boolean | null;
  measures?: string;
}

export interface MiniAmpOtherRisk {
  risk: string;
  measure: string;
  responsible: string;
}

export interface MiniAmpData {
  projectName: string;
  address: string;
  description: string;
  client: string;
  basP: string;
  basU: string;
  basUPhone: string;
  areaName: string;
  delimitation: string;
  adjacentActivity: string;
  risks: MiniAmpSpecialRisk[];
  otherRisks: MiniAmpOtherRisk[];
  rules: string;
  emergency: string;
}

export const emptyMiniAmp = (): MiniAmpData => ({
  projectName: "",
  address: "",
  description: "",
  client: "",
  basP: "",
  basU: "",
  basUPhone: "",
  areaName: "",
  delimitation: "",
  adjacentActivity: "",
  risks: AMP_SPECIAL_RISKS.map((r) => ({
    id: r.id,
    text: r.text,
    applicable: r.applicable ?? null,
    measures: "",
  })),
  otherRisks: [],
  rules:
    "Skyddsutrustning (hjälm, skyddsskor, varselkläder och skyddsglasögon) ska bäras inom arbetsområdet. Arbetsområdet hålls avstängt mot obehöriga. Ordning och städning sker löpande. Tillbud och olyckor rapporteras omgående till Bas-U.",
  emergency: "112 vid akut fara. Närmaste akutmottagning och återsamlingsplats anges vid introduktionen.",
});

interface PlanRef {
  planId: string;
}

const ensurePlan = async (projectId: string, userId: string | null): Promise<PlanRef | null> => {
  const { data } = await supabase
    .from("work_environment_plans")
    .select("id")
    .eq("project_id", projectId)
    .order("created_at", { ascending: false })
    .limit(1);

  const existing = (data || [])[0] as { id: string } | undefined;
  if (existing) return { planId: existing.id };

  const created = await createAmpPlan({
    projectId,
    name: "Arbetsmiljöplan (liten omfattning)",
    userId,
  });
  return created.planId ? { planId: created.planId } : null;
};

const patchSection = async (
  planId: string,
  sectionKey: string,
  patch: { data?: Record<string, unknown>; body?: string }
) => {
  const { data } = await supabase
    .from("amp_sections")
    .select("id, structured_data, body")
    .eq("plan_id", planId)
    .eq("section_key", sectionKey)
    .maybeSingle();

  const row = data as { id: string; structured_data: unknown; body: string | null } | null;
  if (!row) return;

  const current = (row.structured_data as Record<string, unknown>) || {};
  await supabase
    .from("amp_sections")
    .update({
      structured_data: { ...current, ...(patch.data || {}) },
      body: patch.body !== undefined ? patch.body : row.body,
      status: "in_progress",
    } as never)
    .eq("id", row.id);
};

/** Läser tillbaka det som mini-AMP:en tidigare skrivit. */
export const loadMiniAmp = async (projectId: string): Promise<MiniAmpData> => {
  const base = emptyMiniAmp();

  const [{ data: plans }, { data: surveys }] = await Promise.all([
    supabase
      .from("work_environment_plans")
      .select("id")
      .eq("project_id", projectId)
      .order("created_at", { ascending: false })
      .limit(1),
    supabase
      .from("work_area_surveys")
      .select("area_name, delimitation, adjacent_activity")
      .eq("project_id", projectId)
      .order("created_at", { ascending: false })
      .limit(1),
  ]);

  const survey = (surveys || [])[0] as
    | { area_name: string; delimitation: string | null; adjacent_activity: string | null }
    | undefined;
  if (survey) {
    base.areaName = survey.area_name || "";
    base.delimitation = survey.delimitation || "";
    base.adjacentActivity = survey.adjacent_activity || "";
  }

  const planId = ((plans || [])[0] as { id: string } | undefined)?.id;
  if (!planId) return base;

  const { data: sections } = await supabase
    .from("amp_sections")
    .select("section_key, structured_data, body")
    .eq("plan_id", planId);

  ((sections || []) as Array<{
    section_key: string;
    structured_data: Record<string, unknown> | null;
    body: string | null;
  }>).forEach((s) => {
    const d = s.structured_data || {};
    if (s.section_key === "orientering") {
      base.projectName = String(d.projectName || base.projectName);
      base.address = String(d.address || base.address);
      base.description = String(d.description || base.description);
    }
    if (s.section_key === "organisation") {
      const contact = (key: string) => {
        const c = d[key] as { name?: string; phone?: string } | undefined;
        return { name: c?.name || "", phone: c?.phone || "" };
      };
      base.client = contact("client").name;
      base.basP = contact("basP").name;
      const u = contact("basU");
      base.basU = u.name;
      base.basUPhone = u.phone;
      base.emergency = s.body || base.emergency;
    }
    if (s.section_key === "sarskilda_risker") {
      const risks = d.risks as MiniAmpSpecialRisk[] | undefined;
      if (Array.isArray(risks) && risks.length) {
        base.risks = base.risks.map((r) => {
          const match = risks.find((x) => x.id === r.id);
          return match ? { ...r, applicable: match.applicable ?? null, measures: match.measures || "" } : r;
        });
      }
      try {
        const parsed = JSON.parse(s.body || "[]");
        if (Array.isArray(parsed)) base.otherRisks = parsed as MiniAmpOtherRisk[];
      } catch {
        /* fritext från tidigare version – ignoreras */
      }
    }
    if (s.section_key === "introduktion" && s.body) base.rules = s.body;
  });

  return base;
};

/** Skriver mini-AMP:en till AMP-avsnitten och arbetsområdesutredningen. */
export const saveMiniAmp = async (
  projectId: string,
  data: MiniAmpData,
  userId: string | null
): Promise<void> => {
  const plan = await ensurePlan(projectId, userId);
  if (!plan) throw new Error("Kunde inte skapa arbetsmiljöplanen.");

  await patchSection(plan.planId, "orientering", {
    data: {
      projectName: data.projectName,
      address: data.address,
      description: data.description,
    },
  });

  await patchSection(plan.planId, "organisation", {
    data: {
      client: { name: data.client },
      basP: { name: data.basP },
      basU: { name: data.basU, phone: data.basUPhone },
    },
    body: data.emergency,
  });

  await patchSection(plan.planId, "sarskilda_risker", {
    data: { risks: data.risks },
    body: JSON.stringify(data.otherRisks),
  });

  await patchSection(plan.planId, "introduktion", { body: data.rules });

  if (data.areaName.trim()) {
    const { data: existing } = await supabase
      .from("work_area_surveys")
      .select("id")
      .eq("project_id", projectId)
      .order("created_at", { ascending: false })
      .limit(1);

    const row = (existing || [])[0] as { id: string } | undefined;
    const payload = {
      project_id: projectId,
      area_name: data.areaName.trim(),
      delimitation: data.delimitation || null,
      adjacent_activity: data.adjacentActivity || null,
      status: "in_progress",
      created_by: userId,
    };
    if (row) {
      await supabase.from("work_area_surveys").update(payload as never).eq("id", row.id);
    } else {
      await supabase.from("work_area_surveys").insert(payload as never);
    }
  }

  // Processteget markeras som pågående så att färdigställandegraden stämmer.
  await supabase.from("process_steps").upsert(
    {
      project_id: projectId,
      doc_number: "60",
      title: "Arbetsmiljöplan liten omfattning",
      phase: "projektering",
      status: "in_progress",
      not_applicable: false,
      not_applicable_reason: null,
    } as never,
    { onConflict: "project_id,doc_number" }
  );
};

/** Antal ifyllda delar – används för att visa hur långt man kommit. */
export const miniAmpProgress = (data: MiniAmpData) => {
  const steps = [
    Boolean(data.projectName.trim() && data.description.trim()),
    Boolean(data.basU.trim() || data.basP.trim()),
    Boolean(data.areaName.trim()),
    data.risks.some((r) => r.applicable !== null),
    Boolean(data.rules.trim()),
  ];
  return { done: steps.filter(Boolean).length, total: steps.length };
};
